import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from './Button';

const AccessibleModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  closeOnOverlayClick = true 
}) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store previously focused element
      previousFocusRef.current = document.activeElement;
      
      // Focus modal
      modalRef?.current?.focus();

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement?.setAttribute('role', 'status');
      announcement?.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = `${title} dialog opened`;
      document.body?.appendChild(announcement);

      return () => {
        document.body.style.overflow = '';
        document.body?.removeChild(announcement);
        
        // Restore focus
        previousFocusRef?.current?.focus();
      };
    }
  }, [isOpen, title]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  const handleTabKey = (e) => {
    if (!modalRef?.current) return;

    const focusableElements = modalRef?.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );
    const firstElement = focusableElements?.[0];
    const lastElement = focusableElements?.[focusableElements?.length - 1];

    if (e?.shiftKey && document.activeElement === firstElement) {
      e?.preventDefault();
      lastElement?.focus();
    } else if (!e?.shiftKey && document.activeElement === lastElement) {
      e?.preventDefault();
      firstElement?.focus();
    }
  };

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
    full: 'max-w-7xl'
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={closeOnOverlayClick ? onClose : undefined}
      role="presentation"
    >
      <div
        ref={modalRef}
        className={`bg-card rounded-xl shadow-elevation-4 w-full ${sizeClasses?.[size]} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e?.stopPropagation()}
        onKeyDown={handleTabKey}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className="sticky top-0 bg-card border-b px-6 py-4 flex items-center justify-between z-10">
          <h2 id="modal-title" className="text-xl font-semibold text-foreground">
            {title}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close dialog"
          />
        </div>
        <div className="px-6 py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AccessibleModal;